import {
  CSSProperties,
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Box } from '@grupoboticario/flora-react'
import QuickPinchZoom, { UpdateAction } from 'react-quick-pinch-zoom'

import {
  MAX_ZOOM,
  MIN_ZOOM,
  OnZoomEventType,
  createDebouncedFunction,
  getZoomCoordinatesFromEvent,
  getZoomEventType,
  makeTransformValue,
} from 'src/helpers'

import { zoomableInnerClasses } from './Zoomable.style'

export type ZoomableMode = 'desktop' | 'mobile'

export type OnZoomEventReason = 'double-tap' | 'single-click' | 'pinch' | 'reset'

export type OnZoomEventHandler = (
  scale: number,
  zoom: OnZoomEventType,
  reason: OnZoomEventReason,
) => void

export type OnZoomEndEventHandler = (type: OnZoomEventType, reason: OnZoomEventReason) => void

export interface ZoomableProps {
  mode?: ZoomableMode
  style?: CSSProperties
  maxZoom?: number
  minZoom?: number
  disabled?: boolean
  ignoreClassName?: string
  onZoom?: OnZoomEventHandler
  onZoomStart?: () => void
  onZoomEnd?: OnZoomEndEventHandler
}

export const Zoomable = ({
  mode = 'mobile',
  children,
  disabled,
  style = {},
  minZoom = MIN_ZOOM,
  maxZoom = MAX_ZOOM,
  ignoreClassName = '',
  onZoom,
  onZoomStart,
  onZoomEnd,
}: PropsWithChildren<ZoomableProps>) => {
  const quickPinchRef = useRef<QuickPinchZoom>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const hasMovedRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const [scale, setScale] = useState(minZoom)
  const isDoubleTapRef = useRef(false)
  const isSingleClickRef = useRef(false)
  const isZoomingInRef = useRef(false)

  const isDesktop = mode === 'desktop'
  const scaleIsCloseToMax = Math.round(scale) === maxZoom
  const doubleTapZoomFactor = isDesktop ? 0 : 1

  const innerClassVariant = useMemo(() => {
    if (isDragging && scale > minZoom) {
      return 'dragging'
    }

    if (scaleIsCloseToMax) {
      return 'zoomOut'
    }

    return 'zoomIn'
  }, [isDragging, minZoom, scale, scaleIsCloseToMax])

  const innerClassName = isDesktop
    ? zoomableInnerClasses({ variant: innerClassVariant })
    : undefined

  const toggleWillChange = useCallback(() => {
    const { current: div } = innerRef

    if (!div) {
      return
    }

    requestAnimationFrame(() => {
      div.style.setProperty('will-change', 'auto')
      requestAnimationFrame(() => {
        div.style.setProperty('will-change', 'transform')
      })
    })
  }, [])

  const handleZoomEnd: OnZoomEndEventHandler = useCallback(
    (zoomType, zoomReason) => {
      toggleWillChange()

      isDoubleTapRef.current = false
      isSingleClickRef.current = false

      onZoomEnd?.(zoomType, zoomReason)
    },
    [onZoomEnd, toggleWillChange],
  )

  /**
   * Evento criado para contornar problema do onZoomEnd não ser disparado pelo QuickPinchZoom.
   */
  const onZoomUpdateDelayed = useMemo(
    () => createDebouncedFunction<OnZoomEndEventHandler>(handleZoomEnd, 300),
    [handleZoomEnd],
  )

  const getZoomReason = (): OnZoomEventReason => {
    if (isDoubleTapRef.current) {
      return 'double-tap'
    }
    if (isSingleClickRef.current) {
      return 'single-click'
    }
    return 'pinch'
  }

  const resetZoom = useCallback(() => {
    if (!innerRef.current) {
      return
    }
    const value = makeTransformValue({ x: 0, y: 0, scale: minZoom })

    setScale(minZoom)

    innerRef.current.style.setProperty('transform', value)

    onZoom?.(minZoom, 'reset', 'reset')
  }, [minZoom, onZoom])

  const onUpdate = useCallback(
    ({ x, y, scale: newScale }: UpdateAction) => {
      if (!innerRef.current || disabled) {
        return
      }
      if (newScale <= minZoom) {
        resetZoom()
        return
      }

      const value = makeTransformValue({ x, y, scale: newScale })

      innerRef.current.style.setProperty('transform', value)

      isZoomingInRef.current = newScale > scale

      setScale(newScale)

      if (isDragging) {
        hasMovedRef.current = true
      }

      const zoomEventType = getZoomEventType(scale, newScale)

      if (zoomEventType == null) {
        return
      }
      const zoomReason = getZoomReason()

      onZoom?.(newScale, zoomEventType, zoomReason)

      onZoomUpdateDelayed(zoomEventType, zoomReason)
    },
    [disabled, scale, isDragging, minZoom, onZoom, onZoomUpdateDelayed, resetZoom],
  )

  const checkIfShouldZoom = useCallback(
    (ev: MouseEvent<HTMLDivElement>) => {
      const hasMoved = hasMovedRef.current

      const targetAsNode = ev.target as unknown as HTMLDivElement

      const targetHasIgnoredElements =
        targetAsNode.classList.contains(ignoreClassName) || targetAsNode.nodeName === 'SPAN'

      hasMovedRef.current = false

      return !hasMoved && !targetHasIgnoredElements
    },
    [ignoreClassName],
  )

  const handleDragStart = () => {
    setIsDragging(true)
  }

  const handleDragUpdate = useCallback(() => {
    if (!isDesktop) {
      return
    }

    if (isDragging) {
      hasMovedRef.current = true
    }
  }, [isDesktop, isDragging])

  const handleDragEnd = () => {
    setIsDragging(false)

    toggleWillChange()
  }

  const handleContainerMouseDown = useCallback(() => {
    if (!isDesktop) {
      return
    }

    hasMovedRef.current = false
  }, [isDesktop])

  const handleContainerMouseUp = useCallback(
    (ev: MouseEvent<HTMLDivElement>) => {
      if (!isDesktop || !innerRef.current || !quickPinchRef.current) {
        return
      }

      if (!checkIfShouldZoom(ev)) {
        return
      }

      const scaleFactor = scaleIsCloseToMax ? minZoom : maxZoom

      isSingleClickRef.current = true
      isDoubleTapRef.current = false

      const { x, y } = getZoomCoordinatesFromEvent(ev, innerRef.current)

      quickPinchRef.current.scaleTo({
        x,
        y,
        scale: scaleFactor,
        animated: true,
      })
    },
    [checkIfShouldZoom, isDesktop, maxZoom, minZoom, scaleIsCloseToMax],
  )

  const handleDoubleTap = () => {
    isDoubleTapRef.current = true
    isSingleClickRef.current = false
  }

  useEffect(() => {
    if (disabled && innerRef.current) {
      resetZoom()
    }
  }, [disabled, resetZoom])

  return (
    <QuickPinchZoom
      ref={quickPinchRef}
      containerProps={{
        style: {
          touchAction: 'auto',
          pointerEvents: 'auto',
          ...style,
        },
      }}
      doubleTapZoomOutOnMaxScale={!isDesktop}
      draggableUnZoomed={false}
      enabled={!disabled}
      inertia={false}
      isTouch={() => !isDesktop}
      maxZoom={maxZoom}
      minZoom={minZoom}
      shouldInterceptWheel={() => true}
      tapZoomFactor={doubleTapZoomFactor}
      wheelScaleFactor={0}
      zoomOutFactor={1}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
      onUpdate={onUpdate}
      onDoubleTap={handleDoubleTap}
      onZoomStart={onZoomStart}
    >
      <Box
        ref={innerRef}
        css={{ height: '100%' }}
        className={innerClassName}
        onMouseDown={handleContainerMouseDown}
        onMouseUp={handleContainerMouseUp}
      >
        {children}
      </Box>
    </QuickPinchZoom>
  )
}
